import apiClient from './client';

export interface ChecklistItem {
  id: string;
  category: string;
  title: string;
  description: string;
  status: 'GOOD' | 'CONCERN' | 'ISSUE' | 'NOT_CHECKED';
  notes?: string;
}

export interface HealthScore {
  score: number;
  status: string;
  concerns: number;
  issues: number;
  totalItems: number;
}

export interface HealthChecklist {
  id: string;
  birdId: string;
  checklistType: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'EMERGENCY';
  checklistDate: string;
  status: 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  checklistItems: ChecklistItem[];
  overallStatus?: string;
  notes?: string;
  healthScore?: HealthScore;
  completedBy?: string;
  completedAt?: string;
  recommendations: string[];
  actionItems: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateHealthChecklistDto {
  birdId: string;
  checklistType: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'EMERGENCY';
  checklistDate: string;
  checklistItems: ChecklistItem[];
  notes?: string;
}

export interface CreateBatchHealthChecklistDto {
  birdIds: string[];
  checklistType: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'EMERGENCY';
  checklistDate: string;
  checklistItems: ChecklistItem[];
  notes?: string;
}

export interface UpdateHealthChecklistDto {
  checklistItems?: ChecklistItem[];
  status?: 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  notes?: string;
}

export interface HealthTrends {
  trends: Array<{
    date: string;
    score: number;
    status: string;
    concerns: number;
    issues: number;
  }>;
  averageScore: number;
  totalChecklists: number;
  completionRate: number;
}

export interface FlockHealthSummary {
  totalChecklists: number;
  averageScore: number;
  healthDistribution: {
    excellent: number;
    good: number;
    fair: number;
    poor: number;
  };
  recentTrends: {
    recentAverage: number;
    olderAverage: number;
    trend: 'improving' | 'declining' | 'stable';
    change: number;
  };
  topConcerns: string[];
}

export const healthChecklistsApi = {
  // Create a new health checklist
  create: async (data: CreateHealthChecklistDto): Promise<HealthChecklist> => {
    const response = await apiClient.post<HealthChecklist>('/health-checklists', data);
    return response.data;
  },

  createBatch: async (data: CreateBatchHealthChecklistDto): Promise<HealthChecklist[]> => {
    const response = await apiClient.post<HealthChecklist[]>('/health-checklists/batch', data);
    return response.data;
  },

  // Get all health checklists
  getAll: async (params?: {
    birdId?: string;
    type?: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'EMERGENCY';
  }): Promise<HealthChecklist[]> => {
    const response = await apiClient.get<HealthChecklist[]>('/health-checklists', params);
    return response.data;
  },

  // Get a specific health checklist
  getById: async (id: string): Promise<HealthChecklist> => {
    const response = await apiClient.get<HealthChecklist>(`/health-checklists/${id}`);
    return response.data;
  },

  // Update a health checklist
  update: async (id: string, data: UpdateHealthChecklistDto): Promise<HealthChecklist> => {
    const response = await apiClient.patch<HealthChecklist>(`/health-checklists/${id}`, data);
    return response.data;
  },

  // Delete a health checklist
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/health-checklists/${id}`);
  },

  // Get health trends for a specific bird
  getHealthTrends: async (birdId: string, days: number = 30): Promise<HealthTrends> => {
    const response = await apiClient.get<HealthTrends>(`/health-checklists/bird/${birdId}/trends`, { days: days.toString() });
    return response.data;
  },

  // Get flock health summary
  getFlockHealthSummary: async (): Promise<FlockHealthSummary> => {
    const response = await apiClient.get<FlockHealthSummary>('/health-checklists/flock/summary');
    return response.data;
  },

  // Get checklists by bird
  getByBird: async (birdId: string): Promise<HealthChecklist[]> => {
    const response = await apiClient.get<HealthChecklist[]>('/health-checklists', { birdId });
    return response.data;
  },

  // Get checklists by type
  getByType: async (type: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'EMERGENCY'): Promise<HealthChecklist[]> => {
    const response = await apiClient.get<HealthChecklist[]>('/health-checklists', { type });
    return response.data;
  },

  // Complete a checklist
  complete: async (id: string, notes?: string): Promise<HealthChecklist> => {
    return healthChecklistsApi.update(id, {
      status: 'COMPLETED',
      notes
    });
  },

  // Get recent checklists
  getRecent: async (limit: number = 10): Promise<HealthChecklist[]> => {
    const response = await apiClient.get<HealthChecklist[]>('/health-checklists', { limit: limit.toString() });
    return response.data;
  },

  // Get pending checklists
  getPending: async (): Promise<HealthChecklist[]> => {
    const response = await apiClient.get<HealthChecklist[]>('/health-checklists', { status: 'IN_PROGRESS' });
    return response.data;
  }
};
